/* eslint-disable no-mixed-spaces-and-tabs */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import BASE_QUERY from '@/features/baseQuery';
const FORM_CONFIG_TAG = 'FORM_CONFIG';
const FORM_FIELD_CONFIG_TAG = 'FORM_FIELDS';

const FIELD_ENDPOINT = 'formFields';
const CONFIG_ENDPOINT = 'fieldConfig';

// Creating an API service using RTK Query
export const configApi = createApi({
	reducerPath: 'configApi',
	tagTypes: [FORM_CONFIG_TAG, FORM_FIELD_CONFIG_TAG],
	baseQuery: fetchBaseQuery({
		...BASE_QUERY,
		baseUrl: process.env.REACT_APP_CONFIG_API,
	}),
	endpoints: (builder) => ({
		getFormFields: builder.query({
			providesTags: (result, error, arg) => {
				const id = [arg?.configType, arg?.group].filter(Boolean);
				return [{ type: FORM_FIELD_CONFIG_TAG, id: id.length > 0 ? id.join('-') : 'LIST' }];
			},
			query: (params) => ({
				url: `/${FIELD_ENDPOINT}/list`,
				method: 'POST',
				body: params,
			}),
			transformResponse: (response) => {
				console.log('getFormFields', response);
				return response;
			},
		}),
		getFormConfig: builder.query({
			providesTags: (result, error, arg) => [
				{ type: FORM_CONFIG_TAG, id: 'LIST' },
				...(arg?.configType ? [{ type: FORM_CONFIG_TAG, id: arg?.configType }] : []),
			],
			queryFn: async ({ configType, group }, { getState, dispatch }, opts, baseQuery) => {
				const q = await baseQuery({
					url: `/${CONFIG_ENDPOINT}/list`,
					method: 'POST',
					body: { configType },
				});
				if (q.error) return { error: q.error };

				const formConfig = q.data ?? [];

				const fields = await dispatch(
					configApi.endpoints.getFormFields.initiate({
						group,
						configType,
					}),
				).unwrap();

				const mergedFields = formConfig
					.map((doc) => {
						const field = fields.find((d) => d.name === doc.name);
						if (!field) return null;
						// console.log('DOC', doc);
						return {
							field,
							...doc,
							label: doc.label ?? field.label,
							group: field.group,
							name: field.name,
							source: field.source,
							order: doc.order != null ? doc.order : field.order,
							layout: field.layout ?? 'normal',
							configType: field.configType,
							description: doc.description ?? field.description,
							helperText: doc.helperText ?? field.helperText,
							...(field.dependentOn && { dependentOn: field.dependentOn }),
						};
					})
					.filter(Boolean);

				console.log('FIELDS', fields, 'FORM CONFIG', formConfig, 'MERGED', mergedFields);

				return {
					data: mergedFields,
				};
			},
		}),
		saveFormConfig: builder.mutation({
			query: ({ docId, properties = {} }) => ({
				url: `/${CONFIG_ENDPOINT}/update`,
				method: 'POST',
				body: { docId, properties },
			}),
			transformResponse: (response) => {
				console.log('saveFormConfig', response);
				return response;
			},
			onQueryStarted: async (
				{ docId, properties, configType },
				{ dispatch, getState, queryFulfilled },
			) => {
				const cachedArgs = configApi.util
					.selectCachedArgsForQuery(getState(), 'getFormConfig')
					.filter((args) => args?.configType === configType);

				const patches = cachedArgs.map((args) =>
					dispatch(
						configApi.util.updateQueryData('getFormConfig', args, (draft) => {
							return draft.map((d) =>
								d.id === docId
									? {
											...d,
											...properties,
										}
									: d,
							);
						}),
					),
				);
				try {
					const { data } = await queryFulfilled;
					console.log('Res', data);
					if (data.docId && data.docId != docId) {
						try {
							cachedArgs.forEach((args) =>
								dispatch(
									configApi.util.updateQueryData(
										'getFormConfig',
										args,
										(draft) => {
											return draft.map((d) =>
												d.id === docId
													? {
															...d,
															id: data.docId,
															scope: data.scope,
															scopeId: data.scopeId,
														}
													: d,
											);
										},
									),
								),
							);
						} catch (e) {
							console.error('Error patching form config', e);
						}
					}
				} catch (e) {
					patches.forEach((patch) => patch.undo());
				}
			},
		}),
		// Copies the configs into each broker group's own config for those fields, replacing it
		batchCopyFormConfig: builder.mutation({
			query: ({ docIds, brokerGroupIds }) => ({
				url: `/${CONFIG_ENDPOINT}/batch/copy`,
				method: 'POST',
				body: { docIds, brokerGroupIds },
			}),
			invalidatesTags: (result, error, arg) => [
				{ type: FORM_CONFIG_TAG, id: 'LIST' },
				...(arg?.configType ? [{ type: FORM_CONFIG_TAG, id: arg.configType }] : []),
			],
		}),
		// Deletes the user's broker groups' configs, so those fields follow the ledger or
		// default again
		batchResetFormConfig: builder.mutation({
			query: ({ docIds }) => ({
				url: `/${CONFIG_ENDPOINT}/batch/reset`,
				method: 'POST',
				body: { docIds },
			}),
			invalidatesTags: (result, error, arg) => [
				{ type: FORM_CONFIG_TAG, id: 'LIST' },
				...(arg?.configType ? [{ type: FORM_CONFIG_TAG, id: arg.configType }] : []),
			],
		}),
		// Replaces copies with the current settings of the configs they were copied from
		batchResyncFormConfig: builder.mutation({
			query: ({ docIds }) => ({
				url: `/${CONFIG_ENDPOINT}/batch/resync`,
				method: 'POST',
				body: { docIds },
			}),
			invalidatesTags: (result, error, arg) => [
				{ type: FORM_CONFIG_TAG, id: 'LIST' },
				...(arg?.configType ? [{ type: FORM_CONFIG_TAG, id: arg.configType }] : []),
			],
		}),
	}),
});

/** Refetches the user's form configs, e.g. after an admin changes configs. */
export const invalidateFormConfigs = () =>
	configApi.util.invalidateTags([{ type: FORM_CONFIG_TAG, id: 'LIST' }]);

// Exporting auto-generated hooks for the defined endpoints
export const {
	useGetFormFieldsQuery,
	useGetFormConfigQuery,
	useLazyGetFormConfigQuery,
	useSaveFormConfigMutation,
	useBatchCopyFormConfigMutation,
	useBatchResetFormConfigMutation,
	useBatchResyncFormConfigMutation,
} = configApi;
